
import classNames from 'classnames';
import { App, Card } from 'antd';
import { useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { GoCheckCircleFill } from 'react-icons/go';
import { Flex } from '@/components';
import { useSurveyParticipateContext } from './contexts';
import { useTranslation } from 'react-i18next';

export interface SurveyQuestionChoiceProps {
  index: number;
  question: SurveyQuestionMultipleChoiceEntity;
  choice: SurveyQuestionChoiceEntity;
}

const numClassname = 'w-[25px] h-[25px] font-bold bg-stone-300 rounded-md text-xs';
const cardBaseClassName = classNames(
  'text-sm text-[#555] cursor-pointer select-none',
  'border-2 border-solid transition transition-all'
);

export default function SurveyQuestionChoice({
  index,
  question,
  choice,
}: SurveyQuestionChoiceProps) {

  const [t] = useTranslation(['common', 'survey']);
  const { answers, addChoice, removeChoiceById } = useSurveyParticipateContext();

  const { message } = App.useApp();
  const selected = useMemo(() => answers.find(a => a.answerIds.includes(choice.id)), [answers]);

  const onCardClick = useCallback(() => {
    if (selected) {
      return removeChoiceById(question, choice.id);
    }
    const selectedQuestion = answers.find(a => a.questionId === question.id);
    if (!question.allowMultipleSelect) {
      for (const id of (selectedQuestion?.answerIds ?? [])) {
        removeChoiceById(question, id);
      }
      return addChoice(question, choice);
    }
    if (selectedQuestion?.answerIds?.length >= question.maxSelectableChoices) {
      message.error({ key: 'add-choice-err-key', content: t('survey:errors.limit-select-options') });
      return;
    }
    return addChoice(question, choice);
  }, [selected, question, choice, answers, message, t, addChoice, removeChoiceById]);

  const cardClassName = classNames(
    cardBaseClassName,
    selected ? 'border-nurture-darker bg-[#ecf1ec]' : 'border-transparent'
  );

  return (
    <Flex direction="column">
      <Card
        size="small"
        className={cardClassName}
        onClick={onCardClick}
      >
        <Flex align="center">
          <Flex
            align="center"
            justify="center"
            component="span"
            className={numClassname}
          >
            {index + 1}
          </Flex>
          <span>{choice.name}</span>
          <AnimatePresence mode="wait">
            {selected &&
              <motion.div
                className="inline-flex ms-auto"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
              >
                <GoCheckCircleFill className="text-xl text-nurture-darker" />
              </motion.div>
            }
          </AnimatePresence>
        </Flex>
      </Card>
    </Flex >
  );
}
